import type { ReactNode, CSSProperties, ButtonHTMLAttributes } from 'react';
import { Star, CheckCircle2 } from 'lucide-react';
import { SafeImage } from '../../sor/primitives/SafeImage';

// ─── Button ────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'inverse';
type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

export function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  children,
  style,
  ...rest
}: ButtonProps) {
  const sizeStyles: Record<ButtonSize, CSSProperties> = {
    sm: { padding: '0.5rem 1rem', fontSize: '0.8125rem' },
    md: { padding: '0.75rem 1.375rem', fontSize: '0.9375rem' },
    lg: { padding: '1rem 1.75rem', fontSize: '1rem' },
  };

  const variantStyles: Record<ButtonVariant, CSSProperties> = {
    primary: {
      background: 'var(--color-accent-rosegold)',
      color: 'var(--color-text-inverse)',
      border: '1px solid var(--color-accent-rosegold)',
    },
    secondary: {
      background: 'var(--color-background-card)',
      color: 'var(--color-text-primary)',
      border: '1px solid var(--color-border-medium)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--color-text-primary)',
      border: '1px solid transparent',
    },
    inverse: {
      background: 'var(--color-text-primary)',
      color: 'var(--color-text-inverse)',
      border: '1px solid var(--color-text-primary)',
    },
  };

  return (
    <button
      {...rest}
      style={{
        cursor: 'pointer',
        fontFamily: 'var(--font-family-body)',
        fontWeight: 500,
        letterSpacing: '0.005em',
        borderRadius: 'var(--radius-md)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'all 200ms ease',
        ...sizeStyles[size],
        ...variantStyles[variant],
        ...style,
      }}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}

// ─── Page shell ────────────────────────────────────────────────────────────

export function PageSection({
  children,
  maxWidth = 1240,
  paddingY = '5rem',
  background,
  style,
}: {
  children: ReactNode;
  maxWidth?: number;
  paddingY?: string;
  background?: string;
  style?: CSSProperties;
}) {
  return (
    <section
      style={{
        paddingTop: paddingY,
        paddingBottom: paddingY,
        background,
        ...style,
      }}
    >
      <div
        style={{
          maxWidth: `${maxWidth}px`,
          margin: '0 auto',
          padding: '0 1.5rem',
        }}
      >
        {children}
      </div>
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  align = 'left',
  action,
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  align?: 'left' | 'center';
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '2rem',
        marginBottom: '3rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ maxWidth: '640px', textAlign: align }}>
        {eyebrow && (
          <div
            style={{
              fontFamily: 'var(--font-family-body)',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--color-accent-rosegold)',
              marginBottom: '0.875rem',
            }}
          >
            {eyebrow}
          </div>
        )}
        <h2
          style={{
            fontFamily: 'var(--font-family-display)',
            fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-0.015em',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          {title}
        </h2>
        {description && (
          <p
            style={{
              fontSize: '1.0625rem',
              lineHeight: 1.6,
              color: 'var(--color-text-secondary)',
              marginTop: '1rem',
              marginBottom: 0,
            }}
          >
            {description}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

// ─── Rating ─────────────────────────────────────────────────────────────────

export function RatingLine({
  rating,
  reviewCount,
  size = 14,
}: {
  rating: number;
  reviewCount?: number;
  size?: number;
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.375rem',
        fontSize: '0.875rem',
        color: 'var(--color-text-primary)',
      }}
    >
      <Star size={size} strokeWidth={1.5} fill="var(--color-warning)" color="var(--color-warning)" />
      <span style={{ fontWeight: 600 }}>{rating.toFixed(1)}</span>
      {reviewCount !== undefined && (
        <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>
          ({reviewCount})
        </span>
      )}
    </div>
  );
}

// ─── Verified badge ────────────────────────────────────────────────────────

export function VerifiedBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.1875rem 0.5rem',
        background: 'var(--color-accent-rosegold-50)',
        color: 'var(--color-accent-rosegold)',
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        borderRadius: '999px',
      }}
    >
      <CheckCircle2 size={11} strokeWidth={2} />
      Verified
    </span>
  );
}

// ─── Tag pill ──────────────────────────────────────────────────────────────

export function Tag({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.25rem 0.625rem',
        background: 'var(--color-background-surface)',
        border: '1px solid var(--color-border-light)',
        color: 'var(--color-text-secondary)',
        fontSize: '0.75rem',
        fontWeight: 500,
        borderRadius: '999px',
      }}
    >
      {children}
    </span>
  );
}

// ─── Service card ──────────────────────────────────────────────────────────

export function ServiceCard({
  image,
  title,
  vendorName,
  vendorAvatar,
  rating,
  reviewCount,
  priceFrom,
  onClick,
}: {
  image: string;
  title: string;
  vendorName: string;
  vendorAvatar: string;
  rating: number;
  reviewCount: number;
  priceFrom: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'block',
        background: 'var(--color-background-card)',
        border: '1px solid var(--color-border-light)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        transition: 'transform 250ms ease, box-shadow 250ms ease, border-color 250ms ease',
      }}
      className="service-card"
    >
      <div style={{ aspectRatio: '4/3', overflow: 'hidden', position: 'relative' }}>
        <SafeImage
          src={image}
          alt={title}
          fallbackHint={title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
      <div style={{ padding: '1.125rem 1.25rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
          <div
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '999px',
              background: `url(${vendorAvatar}) center/cover no-repeat`,
            }}
          />
          <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
            {vendorName}
          </span>
        </div>
        <div
          style={{
            fontFamily: 'var(--font-family-body)',
            fontSize: '0.9375rem',
            fontWeight: 500,
            lineHeight: 1.45,
            color: 'var(--color-text-primary)',
            marginBottom: '0.75rem',
            minHeight: '2.65em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
          }}
        >
          <RatingLine rating={rating} reviewCount={reviewCount} />
          <div
            style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--color-text-muted)',
            }}
          >
            from{' '}
            <span style={{ color: 'var(--color-text-primary)', fontWeight: 600, fontSize: '0.9375rem', letterSpacing: 0, textTransform: 'none' }}>
              {priceFrom.toLocaleString()} ₼
            </span>
          </div>
        </div>
      </div>
      <style>{`
        .service-card:hover {
          transform: translateY(-3px);
          border-color: var(--color-border-medium) !important;
          box-shadow: var(--shadow-medium);
        }
      `}</style>
    </button>
  );
}

// ─── Category tile ─────────────────────────────────────────────────────────

export function CategoryTile({
  name,
  tagline,
  image,
  serviceCount,
  onClick,
  size = 'md',
}: {
  name: string;
  tagline: string;
  image: string;
  serviceCount: number;
  onClick: () => void;
  size?: 'sm' | 'md' | 'lg';
}) {
  const aspect = size === 'lg' ? '5/6' : size === 'sm' ? '4/3' : '3/4';
  return (
    <button
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: 'pointer',
        position: 'relative',
        display: 'block',
        aspectRatio: aspect,
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        background: 'var(--color-background-surface)',
        transition: 'transform 350ms ease',
      }}
      className="category-tile"
    >
      <SafeImage
        src={image}
        alt={name}
        fallbackHint={name}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(28, 27, 26, 0) 35%, rgba(28, 27, 26, 0.78) 100%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '1.5rem 1.5rem 1.375rem',
          color: 'var(--color-text-inverse)',
        }}
      >
        <div
          style={{
            fontSize: '0.6875rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            opacity: 0.8,
            marginBottom: '0.5rem',
          }}
        >
          {serviceCount} services
        </div>
        <div
          style={{
            fontFamily: 'var(--font-family-display)',
            fontSize: size === 'lg' ? '1.75rem' : '1.375rem',
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
            marginBottom: '0.5rem',
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: '0.875rem',
            lineHeight: 1.45,
            opacity: 0.85,
            maxWidth: '280px',
          }}
        >
          {tagline}
        </div>
      </div>
      <style>{`
        .category-tile:hover { transform: scale(1.015); }
      `}</style>
    </button>
  );
}

// ─── Avatar ────────────────────────────────────────────────────────────────

export function Avatar({
  src,
  size = 44,
  alt = '',
}: {
  src: string;
  size?: number;
  alt?: string;
}) {
  return (
    <div
      role="img"
      aria-label={alt}
      style={{
        width: size,
        height: size,
        borderRadius: '999px',
        background: `url(${src}) center/cover no-repeat`,
        border: '1px solid var(--color-border-light)',
        flexShrink: 0,
      }}
    />
  );
}
